import {
  correspondenceListCorrespondenceThreadsInfiniteOptions,
  useHeyApi,
} from "@platform/api";
import { useInfiniteQuery } from "@tanstack/react-query";

import type { CorrespondenceThread } from "@platform/api";

/**
 * How many threads one page carries.
 *
 * The service caps whatever is asked for at 200. A drawer shows about a
 * dozen rows at a time, so a page an order of magnitude past the viewport is
 * enough to scroll through without a second round trip, and far short of the
 * cap.
 */
const PAGE_SIZE = 50;

export interface CorrespondenceThreads {
  threads: CorrespondenceThread[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Present only while the service has issued a cursor for another page. */
  loadMore?: () => void;
  isLoadingMore: boolean;
}

/**
 * The threads on one RFP.
 *
 * Paged rather than read whole: `nextCursor` is absent on the last page and
 * present otherwise, so paging stops on its absence rather than on a short
 * page — a page exactly as long as the limit would get that wrong.
 *
 * `rfpId` is the RFP's canonical identifier (its UUID), not the numeric id in
 * the workbench URL. The listing refuses anything else.
 */
export const useCorrespondenceThreads = (
  rfpId: string | undefined,
): CorrespondenceThreads => {
  const { centroPortalClient } = useHeyApi();

  const options = correspondenceListCorrespondenceThreadsInfiniteOptions({
    query: { rfpId, limit: PAGE_SIZE },
    client: centroPortalClient,
  });

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...options,
    enabled: !!rfpId,
    initialPageParam: undefined,
    getNextPageParam: (lastPage: { nextCursor?: string }) =>
      lastPage.nextCursor
        ? { query: { cursor: lastPage.nextCursor } }
        : undefined,
  });

  const threads =
    data?.pages.flatMap(
      (page: { threads?: CorrespondenceThread[] }) => page.threads ?? [],
    ) ?? [];

  return {
    threads,
    // A drawer opened on an RFP whose identifier has not resolved yet is
    // waiting, not idle: the query is disabled, so react-query reports
    // neither loading nor success.
    isLoading: isLoading || !rfpId,
    isError,
    refetch: () => void refetch(),
    loadMore: hasNextPage ? () => void fetchNextPage() : undefined,
    isLoadingMore: isFetchingNextPage,
  };
};
